import axios from '../Helpers/axios';
import { toast } from 'react-toastify';

class ExperimentsService {
    handleHttpError(error, customMessages = {}) {
        if (error.response) {
            toast.dismiss('connectionAccess');
            const defaultMessages = {
                400: 'Invalid request data',
                403: 'You are not authorized to perform this action',
                404: 'Resource not found',
                422: 'Invalid data provided',
                500: 'Server error. Please try again later.',
            };

            const message = customMessages[error.response.status] || defaultMessages[error.response.status] || 'Something went wrong. Please contact the developer.';
            toast.error(message);
        }
        return error.response?.status === 204 ? true : null;
    }

    getAllExperiments() {
        return axios.get('/api/console/experiments')
            .then((response) => {
                if (response.status === 200) {
                    return response.data.experiments;
                }
            })
            .catch((error) => {
                this.handleHttpError(error);
                throw error;
            });
    }

    createExperiment() {
        return axios.post('/api/console/experiments')
            .then((response) => {
                if (response.status === 201) {
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error);
                throw error;
            });
    }

    getExperiment(experimentId) {
        return axios.get(`/api/console/experiments/${experimentId}`)
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to access this experiment',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    updateExperiment(experimentId, updateData) {
        return axios.patch(`/api/console/experiments/${experimentId}`, updateData)
            .then((response) => {
                if (response.status === 200) {
                    toast.success('Experiment updated successfully.');
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to update this experiment',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    deleteExperiment(experimentId) {
        return axios.delete(`/api/console/experiments/${experimentId}`)
            .then((response) => {
                if (response.status === 204) {
                    toast.success('Experiment deleted successfully.');
                    return true;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to delete this experiment',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    getExperimentDocuments(experimentId) {
        return axios.get(`/api/console/experiments/${experimentId}/documents`)
            .then((response) => {
                if (response.status === 200) {
                    return response.data.documents;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to access these documents',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    uploadExperimentDocuments(experimentId, files) {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('file', file);
        });

        return axios.post(`/api/console/experiments/${experimentId}/documents`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
            .then((response) => {
                if (response.status === 201) {
                    toast.success('Documents uploaded successfully.');
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    400: 'Invalid file format or size',
                    403: 'You are not authorized to upload documents to this experiment',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    deleteExperimentDocument(experimentId, documentId) {
        return axios.delete(`/api/console/experiments/${experimentId}/documents/${documentId}`)
            .then((response) => {
                if (response.status === 204) {
                    toast.success('Document deleted successfully.');
                    return true;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to delete this document',
                    404: 'Document or experiment not found',
                });
                throw error;
            });
    }

    downloadExperimentDocument(experimentId, documentId) {
        return axios.get(`/api/console/experiments/${experimentId}/documents/${documentId}/file`, {
            responseType: 'blob',
        })
            .then((response) => {
                if (response.status === 200) {
                    return {
                        data: response.data,
                        contentType: response.headers['content-type'],
                    };
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to download this document',
                    404: 'Document not found',
                });
                throw error;
            });
    }

    createExperimentRun(experimentId) {
        return axios.post(`/api/console/experiments/${experimentId}/runs`)
            .then((response) => {
                if (response.status === 201) {
                    toast.success('Run created successfully.');
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to create runs for this experiment',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    getExperimentRuns(experimentId) {
        return axios.get(`/api/console/experiments/${experimentId}/runs`)
            .then((response) => {
                if (response.status === 200) {
                    return response.data.runs;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to access runs for this experiment',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    updateExperimentRun(experimentId, runId, updateData) {
        return axios.patch(`/api/console/experiments/${experimentId}/runs/${runId}`, updateData)
            .then((response) => {
                if (response.status === 200) {
                    toast.success('Run updated successfully.');
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to update this run',
                    404: 'Run or experiment not found',
                });
                throw error;
            });
    }

    deleteExperimentRun(experimentId, runId) {
        return axios.delete(`/api/console/experiments/${experimentId}/runs/${runId}`)
            .then((response) => {
                if (response.status === 204) {
                    toast.success('Run deleted successfully.');
                    return true;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to delete this run',
                    404: 'Run or experiment not found',
                });
                throw error;
            });
    }

    uploadExperimentRunResults(experimentId, runId, files) {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('file', file);
        });

        return axios.post(`/api/console/experiments/${experimentId}/runs/${runId}/results`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
            .then((response) => {
                if (response.status === 201) {
                    toast.success('Runs uploaded successfully.');
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    400: 'Invalid file format or size',
                    403: 'You are not authorized to upload runs to this experiment',
                    404: 'Experiment not found',
                    409: 'Document already exists',
                });
                throw error;
            });
    }

    getExperimentRunsResults(experimentId, runId) {
        return axios.get(`/api/console/experiments/${experimentId}/runs/${runId}/results`)
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to access runs for this experiment',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    getExperimentResults(experimentId) {
        return axios.get(`/api/console/experiments/${experimentId}/results`)
            .then((response) => {
                if (response.status === 200) {
                    return response.data.runs;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to access results for this experiment',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    getExperimentStatus(experimentId) {
        return axios.get(`/api/console/experiments/${experimentId}/progress`)
            .then((response) => {
                if (response.status === 200) {
                    return response.data.runs;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to access results for this experiment',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    getExperimentEvaluationStatus(experimentId) {
        return axios.get(`/api/console/experiments/${experimentId}/ranking-status`)
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to access this experiment\'s evaluation status',
                    404: 'Experiment not found',
                });
                throw error;
            });
    }

    getExperimentEvaluation(experimentId, documentId, datapointId) {
        return axios.get(`/api/console/experiments/${experimentId}/rankings`, {
            params: {
                document_id: documentId,
                datapoint_id: datapointId,
            },
        })
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to access these evaluations',
                    404: 'Experiment, document, or datapoint not found',
                    422: 'Invalid parameters provided',
                });
                throw error;
            });
    }

    updateExperimentEvaluation(experimentId, documentId, datapointId, rankings) {
        return axios.put(`/api/console/experiments/${experimentId}/rankings`,
            { rankings },
            {
                params: {
                    document_id: documentId,
                    datapoint_id: datapointId,
                },
            },
        )
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to update these evaluations',
                    404: 'Experiment, document, or datapoint not found',
                    422: 'Invalid evaluation data provided',
                });
                throw error;
            });
    }

    updateGoldStandard(documentId, dataPointId, value) {
        return axios.put(
            `/api/console/experiments/documents/${documentId}/datapoints/${dataPointId}/gold-standard`,
            { value }
        )
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to update this gold standard',
                    404: 'Failed to update gold standard',
                });
                throw error;
            });
    }

    saveDataPointExample(documentId, dataPointId, value) {
        return axios.post(
            `/api/console/experiments/documents/${documentId}/datapoints/${dataPointId}/good-examples`,
            { value }
        )
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }
            })
            .catch((error) => {
                this.handleHttpError(error, {
                    403: 'You are not authorized to save as an example',
                    404: 'Failed to save as an example',
                });
                throw error;
            });
    }
}

export const experimentsService = new ExperimentsService();
